import type { ReactNode } from 'react';
import escochexLogo from '../../../assets/escochex-logo.webp';
import { ThemeToggle } from '../../../shared/theme/ThemeToggle';
import { HeroCarousel, type Slide } from './HeroCarousel';
import './AuthShellLayout.css';

interface AuthShellLayoutProps {
  children: ReactNode;
  /** EPN-36 Partner Sign In override — default is Admin Sign In's own approved 3 marketing slides,
   * forwarded to HeroCarousel unchanged. */
  heroSlides?: Slide[] | undefined;
}

// A real, already-used destination elsewhere in this codebase (see dashboard-v2/types.ts's
// learnMoreUrl entries and SupportPage.tsx), unlike Forgot Password/Sign Up/Terms/Privacy below, which
// have no approved in-app or external destination yet.
const MARKETING_SITE_URL = 'https://www.escochecks.com';

/**
 * Shared split-screen shell for every EPN Identity pre-authentication screen (Sign In now; password
 * recovery, MFA, and the other approved-but-deferred states later) — logo + form slot on the left, a
 * decorative marketing hero panel on the right. No Admin Sign Up implementation exists yet to have
 * reused this from; built fresh against the approved "[Admin] - Sign In Flow" Figma handoff.
 */
export function AuthShellLayout({ children, heroSlides }: AuthShellLayoutProps) {
  return (
    <div className="auth-shell">
      <div className="auth-shell__form-panel">
        {/* The panel itself tracks the approved ~50/50 split (see .auth-shell__form-panel's flex-basis);
            this inner wrapper keeps the actual content at a comfortable reading width instead of
            stretching fields edge-to-edge on wide viewports, matching the Figma composition rather than
            its exact pixel ratio at every size. */}
        <div className="auth-shell__form-inner">
          {/* No approved Figma frame includes a theme control (see ThemeToggle's own doc comment) —
              paired here with the logo as the most natural existing header-like row on this screen. */}
          <div className="auth-shell__header-row">
            <img src={escochexLogo} alt="Escochex — A Payroll Software Company" className="auth-shell__logo" />
            <ThemeToggle />
          </div>
          <div className="auth-shell__form-slot">{children}</div>
          <p className="auth-shell__legal">
            By signing into your Escochex Account you agree to our{' '}
            <span className="auth-shell__placeholder-link">Terms and Conditions</span> and acknowledge our{' '}
            <span className="auth-shell__placeholder-link">Privacy Policy</span>.
          </p>
        </div>
      </div>
      <div className="auth-shell__hero-panel">
        {/* The photo/scrim/content/dots are clipped to this rounded, padded-inset "card" rather than
            filling the hero column edge-to-edge (see this file's Design Reconciliation note on the
            outer border visible in every supplied screenshot — treated as a screenshot/export artifact,
            not reproduced, while this literal contained-card structure is implemented as explicitly
            instructed). */}
        <div className="auth-shell__hero-card">
          {/* A real navigational control, not decorative — left focusable/announced, unlike the carousel
              below. */}
          <a className="auth-shell__back-link" href={MARKETING_SITE_URL} target="_blank" rel="noopener noreferrer">
            ← Back to website
          </a>
          <div aria-hidden="true">
            <HeroCarousel slides={heroSlides} />
          </div>
        </div>
      </div>
    </div>
  );
}
